import { useContext } from "react";
import {
  Experience,
  ExperienceConfiguration,
} from "@ninetailed/experience.js-react";
import useLogger from "src/hooks/useLogger";
import useCatalog from "src/lib/hooks/useCatalog";
import usePriceFormatter from "src/lib/hooks/usePriceFormatter";
import useTypedMutation from "src/lib/hooks/useTypedMutation";
import { AddToCartPayload, CartMutation } from "src/lib/queries/data/cartData";
import {
  addHeapProductTileClicked,
  addHeapProductTileViewed,
} from "src/lib/services/elevar/events";
import { IProductTileProps } from "src/lib/services/elevar/interfaces";
import { OptionRenderingField } from "src/lib/services/util/optionRendering";
import { isTruthy } from "src/lib/util/isTruthy";
import { KeyValue } from "src/lib/util/toKeyValue";

import { CartContext } from "../Cart";

import { AddOn, AddOnProps } from "./AddOn";
import { findMatchingVariant } from "./utils";

export type AddOnWrapperProps = {
  header?: string | null;
  items: {
    item: Omit<AddOnProps["item"], "quantity">;
    analytics: Pick<
      IProductTileProps,
      | "productID"
      | "location"
      | "productTileTitle"
      | "defaultVariantID"
      | "visibleOnPageload"
    >;
    optionMatchConfigs: OptionRenderingField[];
    optionFilters: KeyValue[];
    quantityMatch: KeyValue[];
  }[];
  parentOptions: { name: string; value: string }[];
};

export type AddOnExperiment = {
  id: string;
  experimentable: ExperienceConfiguration<
    Pick<AddOnWrapperProps, "items"> & { id: string }
  >[];
} & AddOnWrapperProps;

export function AddOnWrapper({
  items,
  parentOptions,
  header: rawHeader,
}: AddOnWrapperProps) {
  const { catalog } = useCatalog(items.map(({ item }) => item.id));
  const { logger } = useLogger();
  const formatPrice = usePriceFormatter();
  const setCartOpen = useContext(CartContext);

  const mappedAddOnItems = items
    .map(({ item, ...addOnProps }) => {
      const product = catalog?.[item.id];
      const variant = findMatchingVariant({
        product,
        parentOptions,
        ...addOnProps,
      });

      if (!product || !variant) {
        return undefined;
      }

      const quantityMatch = parseInt(
        addOnProps.quantityMatch.find((match) =>
          parentOptions.some(({ value }) => value === match.key)
        )?.value ?? "1",
        10
      );

      return {
        ...addOnProps,
        item: {
          ...item,
          title: product.productTitle,
          quantity: quantityMatch,
        },
        product,
        variant,
      };
    })
    .filter(isTruthy);

  const { mutate: addToCartMutation } = useTypedMutation(
    [CartMutation.AddToCart],
    {
      onError: (error) => {
        logger.error("Error adding item to cart from add-on", error);
      },
    }
  );

  const addToCartWrapper = (payload: AddToCartPayload) => {
    addToCartMutation(payload);
    setCartOpen(true);
  };

  // TODO: I18n
  const header = rawHeader || "Pairs well with:";

  return mappedAddOnItems.length > 0 ? (
    <div className="flex flex-col ">
      {/* TODO: I18n */}
      <p className="py-4">{header}</p>
      <div className="grid gap-6">
        {mappedAddOnItems.map(
          ({ item, variant, product, analytics }, index) => {
            const analyticsProps = {
              ...analytics,
              productTitle: product.productTitle,
              variantID: variant.id,
              variantTitle: variant.title,
              sku: variant.sku,
              price: formatPrice(variant.price),
              productColorSelected: variant.selectedOptions.find(
                (o) => o.name === "Color"
              )?.value,
              defaultImageUrl:
                product.variants[product.defaultVariant].cardImage?.url,
              displayedImageUrl: variant.cardImage?.url,
              productTileSectionPosition: index + 1,
            };

            return (
              <AddOn
                key={item.id}
                item={item}
                variant={variant}
                product={product}
                onAdd={addToCartWrapper}
                onViewed={
                  // istanbul ignore next
                  () => addHeapProductTileViewed(analyticsProps)
                }
                onClicked={() => addHeapProductTileClicked(analyticsProps)}
              />
            );
          }
        )}
      </div>
    </div>
  ) : null;
}

export function AddOnExperimentWrapper({
  id,
  experimentable,
  header,
  parentOptions,
  items,
}: AddOnExperiment) {
  return (
    <Experience
      id={id}
      component={AddOnWrapper}
      items={items ?? []}
      passthroughProps={{ parentOptions, header }}
      experiences={experimentable}
    />
  );
}
